import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, Alert, ActivityIndicator } from 'react-native';
import { Text } from '@/components';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import { useAuth } from '@/context/AuthContext';
import { firebaseAuthExchange } from '@/lib/api';
import { UserPlus } from 'lucide-react-native';
import { getFirebaseAuth, createUserWithEmailAndPassword, updateProfile, signInWithGoogle } from '@/lib/firebase';
import { logEvent } from '@/lib/analytics';
import * as WebBrowser from 'expo-web-browser';
import * as Google from 'expo-auth-session/providers/google';
import theme from '@/constants/colors';
import styles from './auth.styles';
import { useTranslation } from 'react-i18next';

WebBrowser.maybeCompleteAuthSession();

function normalizeToken(token: any): string | null {
  if (!token) return null;
  if (typeof token === 'string') return token;
  try {
    if (token instanceof ArrayBuffer) {
      const view = new Uint8Array(token);
      let s = '';
      for (let i = 0; i < view.length; i++) s += String.fromCharCode(view[i]);
      return decodeURIComponent(escape(s));
    }
    if (token && typeof token === 'object' && Array.isArray((token as any).data)) {
      const arr = (token as any).data as number[];
      let s = '';
      for (let i = 0; i < arr.length; i++) s += String.fromCharCode(arr[i]);
      return decodeURIComponent(escape(s));
    }
    return String(token);
  } catch (err) {
    return String(token);
  }
}

export default function SignupScreen() {
  const router = useRouter();
  const { signInWithToken } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [request, response, promptAsync] = Google.useAuthRequest({
    webClientId: "447944956309-t92ug0tqs40adop6mnvifqbmfsi7dtj6.apps.googleusercontent.com",
    iosClientId: "603763351756-ekakr88vbtlkqv620q7h8j2h3amq0fem.apps.googleusercontent.com",
    androidClientId: "447944956309-8emn1c0n3qljs8o595g088ddd2q2ibtj.apps.googleusercontent.com",
    clientId: "447944956309-t92ug0tqs40adop6mnvifqbmfsi7dtj6.apps.googleusercontent.com",
    scopes: ['profile', 'email'],
    responseType: 'token',
  });

  React.useEffect(() => {
    (async () => {
      if (response?.type === 'success') {
        try {
          setIsLoading(true);
          // normalize potential ArrayBuffer/bytes to string
          const rawToken = response.authentication?.accessToken || response.authentication?.idToken;
          const tokenStr = normalizeToken(rawToken);
          if (!tokenStr) throw new Error('No access token from Google');
          // Sign into Firebase with the Google token to obtain a Firebase ID token (JWT)
          const fb = await signInWithGoogle(getFirebaseAuth(), tokenStr);
          const session = await firebaseAuthExchange(fb.idToken);
          await signInWithToken(session);
          logEvent('sign_up', { method: 'google' });
          if (true) {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          }
          router.replace('/');
        } catch (err: any) {
          console.error('Google sign-up error', err);
          Alert.alert(t('auth.googleSignInError'), err?.message || String(err));
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [response]);

  const handleSignUp = async () => {
    if (!email || !name || !password) {
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
      Alert.alert(t('common.error'), t('auth.pleaseFillAllFields'));
      return;
    }

    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }

    setIsLoading(true);
    try {
      const credential = await createUserWithEmailAndPassword(
        getFirebaseAuth(),
        email.trim().toLowerCase(),
        password
      );
      if (name.trim()) {
        await updateProfile(credential.user, { displayName: name.trim() });
      }
      const idToken = await credential.user.getIdToken(true);
      const session = await firebaseAuthExchange(idToken);
      await signInWithToken(session);
      logEvent('sign_up', { method: 'email' });
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
      router.replace('/');
    } catch (error: any) {
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
      console.error('Firebase sign-up error:', error);
      Alert.alert(t('common.error'), error.message || t('auth.failedToCreateAccount'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          <UserPlus size={64} color="#5d6b4a" />
          <Text style={styles.title}>{t('auth.createAccount')}</Text>
          <Text style={styles.subtitle}>{t('auth.signUpToStart')}</Text>
        </View>

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>{t('auth.name')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('auth.yourName')}
              value={name}
              onChangeText={setName}
              autoCapitalize="words"
              editable={!isLoading}
              placeholderTextColor={theme.textMuted}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>{t('auth.email')}</Text>
            <TextInput
              style={styles.input}
              placeholder="your@email.com"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              editable={!isLoading}
              placeholderTextColor={theme.textMuted}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>{t('auth.password')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('auth.createAPassword')}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password"
              editable={!isLoading}
              placeholderTextColor={theme.textMuted}
            />
          </View>

          <TouchableOpacity
            style={[styles.button, isLoading && styles.buttonDisabled]}
            onPress={handleSignUp}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color={theme.backgroundPrimary} />
            ) : (
              <Text style={styles.buttonText}>{t('auth.createAccount')}</Text>
            )}
          </TouchableOpacity>

          {/* <TouchableOpacity
            style={[styles.button, { marginTop: 8, backgroundColor: '#db4437' }]}
            onPress={async () => {
              try {
                if (true) {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                }
                setIsLoading(true);
                // Use native flow (no proxy) for dev-client / standalone.
                await promptAsync();
              } catch (err: any) {
                console.error('Prompt Google Auth error', err);
                Alert.alert(t('common.error'), err?.message || String(err));
                setIsLoading(false);
              }
            }}
            disabled={!request || isLoading}
          >
            <Text style={styles.buttonText}>{t('auth.continueWithGoogle')}</Text>
          </TouchableOpacity> */}

          <View style={styles.footer}>
            <Text style={styles.footerText}>{t('auth.alreadyHaveAccount')}</Text>
            <TouchableOpacity onPress={() => {
              if (true) {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              }
              router.push('/login');
            }}>
              <Text style={styles.linkText}>{t('auth.signIn')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}